import { useState, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, formatBytes, getDownloadUrl, getErrorMessage, uploadObject } from '../api/client';
import type { AccessInfo, BlobStats, BucketInfo, ObjectItem, ObjectsPage } from '../api/types';
import { AccessList } from '../components/AccessList';

// Mirrors the Worker's single-streamed admin-upload cap (95 MB). Larger files
// must use the C# client / CLI (multipart), so the browser upload is disabled.
const UPLOAD_MAX_BYTES = 95 * 1024 * 1024;

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-navy">{value}</div>
    </div>
  );
}

function Breadcrumb({ prefix, onNavigate }: { prefix: string; onNavigate: (p: string) => void }) {
  const parts = prefix.split('/').filter(Boolean);
  return (
    <div className="text-sm text-gray-600">
      <button className="text-navy hover:underline" onClick={() => onNavigate('')}>root</button>
      {parts.map((part, i) => {
        const next = parts.slice(0, i + 1).join('/') + '/';
        return (
          <span key={next}>
            {' / '}
            <button className="text-navy hover:underline" onClick={() => onNavigate(next)}>{part}</button>
          </span>
        );
      })}
    </div>
  );
}

export function Explorer() {
  const qc = useQueryClient();
  const buckets = useQuery({
    queryKey: ['buckets'],
    queryFn: () => api<{ buckets: BucketInfo[] }>('/api/admin/buckets'),
  });
  // Bucket / folder / selected object live in the URL, so refresh, back/forward,
  // and link-sharing all restore the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const bucket = searchParams.get('bucket') ?? '';
  const prefix = searchParams.get('prefix') ?? '';
  const selectedKey = searchParams.get('key') ?? '';

  // Upload drawer state. In "update" mode the drawer replaces one existing object: the key
  // is locked so the new file can only overwrite that exact object, never retarget another.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [objectKey, setObjectKey] = useState('');

  // Delete confirmation modal state.
  const [pendingDelete, setPendingDelete] = useState<ObjectItem | null>(null);

  // Replace confirmation modal state (an update overwrites the object's content).
  const [pendingUpdate, setPendingUpdate] = useState<{ file: File; key: string } | null>(null);

  const active = bucket || buckets.data?.buckets[0]?.logicalName || '';

  const navigateTo = (b: string, p: string, key?: string) => {
    const next = new URLSearchParams();
    if (b) next.set('bucket', b);
    if (p) next.set('prefix', p);
    if (key) next.set('key', key);
    setSearchParams(next);
  };
  const objects = useQuery({
    queryKey: ['objects', active, prefix],
    queryFn: () =>
      api<ObjectsPage>(
        `/api/admin/buckets/${encodeURIComponent(active)}/objects?prefix=${encodeURIComponent(prefix)}`,
      ),
    enabled: active.length > 0,
  });

  // File details drawer: derived from the ?key= param once the listing loads,
  // so a refresh (or shared link) reopens the same drawer.
  const details = selectedKey
    ? objects.data?.items.find((o) => o.key === selectedKey) ?? null
    : null;

  // Who can access the current folder.
  const folderAccess = useQuery({
    queryKey: ['access', active, 'prefix', prefix],
    queryFn: () =>
      api<AccessInfo>(
        `/api/admin/access?bucket=${encodeURIComponent(active)}&prefix=${encodeURIComponent(prefix)}`,
      ),
    enabled: active.length > 0,
  });

  // Who can access the selected file (details drawer).
  const fileAccess = useQuery({
    queryKey: ['access', active, 'key', details?.key ?? ''],
    queryFn: () =>
      api<AccessInfo>(
        `/api/admin/access?bucket=${encodeURIComponent(active)}&key=${encodeURIComponent(details!.key)}`,
      ),
    enabled: !!details && active.length > 0,
  });

  // Per-blob usage (reads/writes + ingress/egress + recent events) for the drawer.
  const fileStats = useQuery({
    queryKey: ['blobstats', active, details?.key ?? ''],
    queryFn: () =>
      api<BlobStats>(
        `/api/admin/blobstats?bucket=${encodeURIComponent(active)}&key=${encodeURIComponent(details!.key)}`,
      ),
    enabled: !!details && active.length > 0,
  });

  const download = useMutation({
    mutationFn: async (o: ObjectItem) => {
      // Navigate an <a download> to a signed URL so the browser streams the file with its
      // native progress widget, instead of buffering the whole blob in memory first.
      const url = await getDownloadUrl(active, o.key);
      const a = document.createElement('a');
      a.href = url;
      a.download = o.key.split('/').pop() || o.key;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    // The download streams via browser navigation, so the object read is logged server-side
    // only after this mutation resolves. Refresh the drawer's activity shortly after —
    // best-effort, since we can't observe when the streamed download actually finishes.
    onSuccess: (_data, o) =>
      window.setTimeout(() => void qc.invalidateQueries({ queryKey: ['blobstats', active, o.key] }), 1500),
  });

  const del = useMutation({
    mutationFn: (o: ObjectItem) =>
      api<{ deleted: boolean }>(
        `/api/admin/buckets/${encodeURIComponent(active)}/object?key=${encodeURIComponent(o.key)}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, o) => {
      void qc.invalidateQueries({ queryKey: ['objects', active] });
      if (selectedKey === o.key) navigateTo(active, prefix);
      setPendingDelete(null);
    },
  });

  const upload = useMutation({
    mutationFn: (vars: { file: File; key: string }) =>
      uploadObject(
        `/api/admin/buckets/${encodeURIComponent(active)}/object?key=${encodeURIComponent(vars.key)}`,
        vars.file,
      ),
    onSuccess: (_data, vars) => {
      const slash = vars.key.lastIndexOf('/');
      navigateTo(active, slash >= 0 ? vars.key.slice(0, slash + 1) : '');
      void qc.invalidateQueries({ queryKey: ['objects', active] });
      void qc.invalidateQueries({ queryKey: ['blobstats', active, vars.key] });
      setDrawerOpen(false);
      setIsUpdate(false);
      setFile(null);
      setObjectKey('');
    },
  });

  const openDrawer = () => {
    upload.reset();
    setFile(null);
    setObjectKey(prefix);
    setIsUpdate(false);
    setDrawerOpen(true);
  };

  // Open the drawer to replace one existing object: pre-fill and lock its key, then the user
  // just picks the new file. Closes the details drawer first so the update drawer is on top.
  const openUpdate = (o: ObjectItem) => {
    upload.reset();
    setFile(null);
    setObjectKey(o.key);
    setIsUpdate(true);
    setDrawerOpen(true);
    if (selectedKey) navigateTo(active, prefix);
  };

  const closeDrawer = () => {
    if (upload.isPending) return;
    setDrawerOpen(false);
    setIsUpdate(false);
  };

  const onPickFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    if (f) setObjectKey((k) => (k === '' || k.endsWith('/') ? k + f.name : k));
  };

  const trimmedKey = objectKey.trim();
  const keyValid = trimmedKey.length > 0 && !trimmedKey.endsWith('/');
  const canUpload = !!file && keyValid && !upload.isPending;

  const submitUpload = () => {
    if (!file || !keyValid) return;
    if (file.size > UPLOAD_MAX_BYTES) {
      window.alert(
        `"${file.name}" is ${formatBytes(file.size)} — larger than the ${formatBytes(UPLOAD_MAX_BYTES)} browser-upload limit. Use the C# client or CLI.`,
      );
      return;
    }
    // Updating overwrites an existing object's content — confirm before replacing.
    if (isUpdate) {
      setPendingUpdate({ file, key: trimmedKey });
      return;
    }
    upload.mutate({ file, key: trimmedKey });
  };

  const isEmpty =
    objects.data && objects.data.items.length === 0 && objects.data.prefixes.length === 0;

  return (
    <div>
      <h1 className="mb-4 text-xl font-bold text-navy">Explorer</h1>
      <div className="mb-2 flex items-center gap-3">
        <select
          className="rounded border px-2 py-1 text-sm"
          value={active}
          onChange={(e) => navigateTo(e.target.value, '')}
        >
          {!buckets.data ? <option value="">{buckets.isLoading ? 'Loading…' : 'No buckets'}</option> : null}
          {buckets.data?.buckets.map((b) => (
            <option key={b.logicalName} value={b.logicalName}>{b.logicalName}</option>
          ))}
        </select>
        <Breadcrumb prefix={prefix} onNavigate={(p) => navigateTo(active, p)} />
        <div className="ml-auto flex items-center gap-2">
          <button
            className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
            disabled={!active}
            onClick={openDrawer}
          >
            Upload
          </button>
        </div>
      </div>

      {/* Grants that apply to the current folder or anything inside it. */}
      <div className="mb-3 rounded border border-gray-200 bg-white p-3 shadow-sm">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Grants within {prefix ? <span className="font-mono normal-case">{prefix}</span> : 'this bucket'}
        </h3>
        <p className="mb-2 mt-0.5 text-xs text-gray-400">
          Includes grants scoped to paths inside this folder — a narrower scope doesn&apos;t allow
          listing the folder itself, only what its pattern covers.
        </p>
        {folderAccess.isLoading ? (
          <p className="text-sm text-gray-400">Checking…</p>
        ) : folderAccess.isError ? (
          <p className="text-sm text-red-600">{getErrorMessage(folderAccess.error)}</p>
        ) : folderAccess.data ? (
          <AccessList info={folderAccess.data} showAclLink />
        ) : null}
      </div>

      {download.isError ? (
        <p className="mb-2 text-sm text-red-600">Download failed: {getErrorMessage(download.error)}</p>
      ) : null}

      <div className="overflow-hidden rounded border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
              <th className="p-2">Name</th>
              <th className="p-2">Size</th>
              <th className="p-2">Modified</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {objects.data?.prefixes.map((p) => (
              <tr key={p} className="cursor-pointer border-b hover:bg-gray-50" onClick={() => navigateTo(active, p)}>
                <td className="p-2 text-navy">📁 {p.slice(prefix.length)}</td>
                <td className="p-2 text-gray-400">—</td>
                <td className="p-2 text-gray-400">—</td>
                <td className="p-2" />
              </tr>
            ))}
            {objects.data?.items.map((o) => (
              <tr key={o.key} className="border-b hover:bg-gray-50">
                <td className="p-2">
                  <button className="text-left text-navy hover:underline" onClick={() => navigateTo(active, prefix, o.key)}>
                    {o.key.slice(prefix.length)}
                  </button>
                </td>
                <td className="p-2">{formatBytes(o.size)}</td>
                <td className="p-2 text-xs text-gray-500">{new Date(o.uploaded).toLocaleString()}</td>
                <td className="whitespace-nowrap p-2 text-right">
                  <button
                    className="text-navy hover:underline disabled:opacity-50"
                    disabled={download.isPending}
                    onClick={() => download.mutate(o)}
                  >
                    Download
                  </button>
                  <button
                    className="ml-3 text-navy hover:underline disabled:opacity-50"
                    disabled={upload.isPending}
                    onClick={() => openUpdate(o)}
                  >
                    Update
                  </button>
                  <button
                    className="ml-3 text-red-600 hover:underline disabled:opacity-50"
                    disabled={del.isPending}
                    onClick={() => {
                      del.reset();
                      setPendingDelete(o);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {objects.isLoading ? <div className="p-3 text-sm">Loading…</div> : null}
        {objects.isError ? (
          <div className="p-3 text-sm text-red-600">{getErrorMessage(objects.error)}</div>
        ) : null}
        {isEmpty ? <div className="p-3 text-sm text-gray-500">Empty</div> : null}
      </div>

      {/* Upload drawer: pick a file and define its full object key. */}
      {drawerOpen ? (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/30" onClick={closeDrawer} />
          <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h2 className="truncate pr-2 text-lg font-semibold text-navy">
                {isUpdate ? 'Update file' : <>Upload to <span className="font-mono">{active}</span></>}
              </h2>
              <button
                className="text-gray-400 hover:text-gray-700 disabled:opacity-40"
                onClick={closeDrawer}
                disabled={upload.isPending}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 space-y-5 overflow-auto p-5">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">File</label>
                <input type="file" onChange={onPickFile} className="block w-full text-sm" />
                {file ? (
                  <p className="mt-1 text-xs text-gray-500">{file.name} · {formatBytes(file.size)}</p>
                ) : null}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Key (object path)</label>
                <input
                  type="text"
                  value={objectKey}
                  onChange={(e) => setObjectKey(e.target.value)}
                  placeholder="odincli/sql/sql_12313.bak"
                  spellCheck={false}
                  readOnly={isUpdate}
                  className={`block w-full rounded border px-2 py-1.5 font-mono text-sm${
                    isUpdate ? ' cursor-not-allowed bg-gray-100 text-gray-600' : ''
                  }`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canUpload) submitUpload();
                  }}
                />
                {isUpdate ? (
                  <p className="mt-1 text-xs text-amber-700">
                    Replaces the current file at this key with the new version. The key can&apos;t be changed here.
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    Full key within <span className="font-mono">{active}</span>. Use{' '}
                    <span className="font-mono">/</span> to nest folders (e.g.{' '}
                    <span className="font-mono">odincli/sql/sql_12313.bak</span>). Prefilled with the
                    current folder + file name; edit it freely.
                  </p>
                )}
                {objectKey.length > 0 && !keyValid ? (
                  <p className="mt-1 text-xs text-red-600">
                    Enter a non-empty key that doesn&apos;t end with &quot;/&quot;.
                  </p>
                ) : null}
              </div>

              {upload.isError ? (
                <p className="text-sm text-red-600">Upload failed: {getErrorMessage(upload.error)}</p>
              ) : null}
            </div>

            <div className="flex justify-end gap-2 border-t px-5 py-3">
              <button
                className="rounded px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                onClick={closeDrawer}
                disabled={upload.isPending}
              >
                Cancel
              </button>
              <button
                className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                onClick={submitUpload}
                disabled={!canUpload}
              >
                {upload.isPending
                  ? isUpdate ? 'Replacing…' : 'Uploading…'
                  : isUpdate ? 'Upload new version' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* File details drawer: metadata + who has access to this object. */}
      {details ? (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/30" onClick={() => navigateTo(active, prefix)} />
          <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h2 className="truncate pr-2 text-lg font-semibold text-navy" title={details.key}>
                {details.key.split('/').pop() || details.key}
              </h2>
              <button
                className="text-gray-400 hover:text-gray-700"
                onClick={() => navigateTo(active, prefix)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 space-y-5 overflow-auto p-5 text-sm">
              <dl className="space-y-2">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Key</dt>
                  <dd className="break-all font-mono text-xs text-gray-800">{details.key}</dd>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Size</dt>
                    <dd>{formatBytes(details.size)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Modified</dt>
                    <dd className="text-xs">{new Date(details.uploaded).toLocaleString()}</dd>
                  </div>
                </div>
                {details.contentType ? (
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Content type</dt>
                    <dd className="font-mono text-xs">{details.contentType}</dd>
                  </div>
                ) : null}
              </dl>

              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Who has access</h3>
                {fileAccess.isLoading ? (
                  <p className="text-gray-400">Checking…</p>
                ) : fileAccess.isError ? (
                  <p className="text-red-600">{getErrorMessage(fileAccess.error)}</p>
                ) : fileAccess.data ? (
                  <AccessList info={fileAccess.data} showAclLink />
                ) : null}
              </div>

              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Activity</h3>
                {fileStats.isLoading ? (
                  <p className="text-gray-400">Loading…</p>
                ) : fileStats.isError ? (
                  <p className="text-red-600">{getErrorMessage(fileStats.error)}</p>
                ) : fileStats.data ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <Stat label="Reads" value={fileStats.data.stats.reads} />
                      <Stat label="Writes" value={fileStats.data.stats.writes} />
                      <Stat label="Egress (out)" value={formatBytes(fileStats.data.stats.egress_bytes)} />
                      <Stat label="Ingress (in)" value={formatBytes(fileStats.data.stats.ingress_bytes)} />
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Last accessed:{' '}
                      {fileStats.data.stats.last_access_utc
                        ? new Date(fileStats.data.stats.last_access_utc).toLocaleString()
                        : 'never'}
                    </p>
                    {fileStats.data.recent.length > 0 ? (
                      <div className="mt-3">
                        <div className="mb-1 text-xs font-semibold text-gray-500">Recent events</div>
                        <div className="max-h-48 overflow-auto rounded border border-gray-200">
                          <table className="w-full text-xs">
                            <tbody>
                              {fileStats.data.recent.map((r, i) => (
                                <tr key={i} className="border-b last:border-0">
                                  <td className="whitespace-nowrap px-2 py-1 text-gray-500">
                                    {new Date(r.ts_utc).toLocaleString()}
                                  </td>
                                  <td className="px-2 py-1 font-mono">{r.op}</td>
                                  <td className="px-2 py-1 text-gray-600">{r.actor_type}</td>
                                  <td className="px-2 py-1 text-right">{r.bytes != null ? formatBytes(r.bytes) : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-gray-400">No recorded events yet.</p>
                    )}
                  </>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2 border-t px-5 py-3">
              <button
                className="mr-auto rounded px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                onClick={() => {
                  del.reset();
                  setPendingDelete(details);
                }}
                disabled={del.isPending}
              >
                Delete
              </button>
              <button
                className="rounded border border-navy px-3 py-1.5 text-sm font-semibold text-navy hover:bg-navy/5 disabled:opacity-50"
                onClick={() => openUpdate(details)}
                disabled={upload.isPending}
              >
                Update
              </button>
              <button
                className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                onClick={() => download.mutate(details)}
                disabled={download.isPending}
              >
                {download.isPending ? 'Downloading…' : 'Download'}
              </button>
              <button
                className="rounded px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                onClick={() => navigateTo(active, prefix)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Delete confirmation modal (above the drawers). */}
      {pendingDelete ? (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => {
              if (!del.isPending) setPendingDelete(null);
            }}
          />
          <div className="absolute left-1/2 top-1/3 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded bg-white p-5 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-navy">Delete object?</h2>
            <p className="mb-1 break-all font-mono text-xs text-gray-800">{pendingDelete.key}</p>
            <p className="mb-4 text-xs text-gray-500">
              {formatBytes(pendingDelete.size)} · this permanently removes the object from{' '}
              <span className="font-mono">{active}</span>.
            </p>
            {del.isError ? (
              <p className="mb-3 text-sm text-red-600">Delete failed: {getErrorMessage(del.error)}</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <button
                className="rounded px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                onClick={() => setPendingDelete(null)}
                disabled={del.isPending}
              >
                Cancel
              </button>
              <button
                className="rounded bg-red-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                onClick={() => del.mutate(pendingDelete)}
                disabled={del.isPending}
              >
                {del.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Replace confirmation modal (an update overwrites the object's content). */}
      {pendingUpdate ? (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setPendingUpdate(null)} />
          <div className="absolute left-1/2 top-1/3 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded bg-white p-5 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-navy">Replace file?</h2>
            <p className="mb-1 break-all font-mono text-xs text-gray-800">{pendingUpdate.key}</p>
            <p className="mb-4 text-xs text-gray-500">
              Overwrites the current object in <span className="font-mono">{active}</span> with{' '}
              <span className="font-medium text-gray-700">{pendingUpdate.file.name}</span> (
              {formatBytes(pendingUpdate.file.size)}). This can&apos;t be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="rounded px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                onClick={() => setPendingUpdate(null)}
              >
                Cancel
              </button>
              <button
                className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white"
                onClick={() => {
                  const pu = pendingUpdate;
                  setPendingUpdate(null);
                  upload.mutate(pu);
                }}
              >
                Replace
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
